// Animation timing core — SIM STATE, not presentation: all five Animation fields
// (m_nStrip/m_nFrame/m_wf/m_cCountdown/m_cDelay) are MP-validated cross-machine state, and
// frame delays drive UpdateInterval::MinSkip scheduling + kmidAnimationComplete transitions.
// game/Animation.cpp + game/ht.h:1705-1809. Blitting (DrawFrame/GetBounds/GetFrameOrigin)
// is host-side and excluded.
//
// AnimData is the JSON-backed sim slice of AnimationData (.anir metadata extracted by the
// wi.cl pipeline): strip names, default delays, per-frame hold delays, special points.

import { s8 } from '../math/int32.ts';

// game/ht.h:1704-1708
export const kfAniLoop = 0x0001;
export const kfAniDone = 0x0002;
export const kfAniIgnoreFirstAdvance = 0x0004;
export const kfAniResetWhenDone = 0x0008;
export const kfAniFreeAnimationData = 0x8000;

// game/ht.h:1018
export const kctUpdate = 8;

export interface AnimFrameData {
  delay: number; // FrameData.cHold (byte)
  special1: number; // bCustomData1 (raw byte; sign-extended on read)
  special2: number; // bCustomData2
}

export interface AnimStripData {
  name: string;
  defaultDelay: number; // StripData.cDelay (byte)
  frames: AnimFrameData[];
}

export interface Point {
  x: number;
  y: number;
}

// C-locale stricmp: ASCII case fold only
function asciiFold(s: string): string {
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    out += String.fromCharCode(c >= 0x41 && c <= 0x5a ? c + 0x20 : c);
  }
  return out;
}

/** The sim slice of AnimationData (game/Animation.cpp:120-247). gcxTile is the game's tile
 *  size (32 for 832 art, 24 for 824) — a sim input: GetSpecialPoint32 scales only at 24. */
export class AnimData {
  private strips: AnimStripData[];
  private gcxTile: number;
  /** The registry key this data was loaded under (bare name, no .anir) — inert
   *  metadata for the CLIENT renderer's art lookup; nothing sim-side reads it. */
  name = '';

  constructor(strips: AnimStripData[], gcxTile: number) {
    this.strips = strips;
    this.gcxTile = gcxTile;
  }

  GetStripCount(): number {
    return this.strips.length;
  } // Animation.cpp:120-123
  GetFrameCount(nStrip: number): number {
    return this.strips[nStrip].frames.length;
  } // :125-130

  // game/Animation.cpp:142-150 — (char) casts: int8 sign-extension
  GetSpecialPoint(nStrip: number, nFrame: number, ppt: Point): void {
    const f = this.strips[nStrip].frames[nFrame];
    ppt.x = s8(f.special1);
    ppt.y = s8(f.special2);
  }

  // game/Animation.cpp:152-163 — the genuine sim double (sim-quirks A12): in 824 mode (gcxTile==24) scale
  // by the literal 1.3333333333333 with C round() = half AWAY from zero (Math.round is
  // half toward +inf — wrong for negatives). Golden-vectored over the int8 domain.
  GetSpecialPoint32(nStrip: number, nFrame: number, ppt: Point): void {
    this.GetSpecialPoint(nStrip, nFrame, ppt);
    if (this.gcxTile === 24) {
      ppt.x = cRound(ppt.x * 1.3333333333333);
      ppt.y = cRound(ppt.y * 1.3333333333333);
    }
  }

  // game/Animation.cpp:165-172
  GetFrameDelay(nStrip: number, nFrame: number): number {
    return this.strips[nStrip].frames[nFrame].delay;
  }

  // game/Animation.cpp:228-241 — C-locale stricmp, FIRST match in file strip order
  GetStripIndex(pszStripName: string): number {
    const want = asciiFold(pszStripName);
    for (let i = 0; i < this.strips.length; i++) {
      if (asciiFold(this.strips[i].name) === want) return i;
    }
    return -1; // Assert("Strip not found") in DEBUG
  }

  // game/Animation.cpp:243-248
  GetStripDelay(nStrip: number): number {
    return this.strips[nStrip].defaultDelay;
  }
}

/** C round(): half away from zero. */
export function cRound(r: number): number {
  return r < 0 ? -Math.round(-r) : Math.round(r);
}

export class Animation {
  private m_panid: AnimData | null = null;
  private m_nStrip = 0;
  private m_nFrame = 0;
  private m_wf = kfAniDone; // ctor (ht.h:1712-1718)
  private m_cCountdown = 0; // UNSIGNED byte — mask & 0xff on every store
  private m_cDelay = 0; // unsigned byte

  // game/Animation.cpp:253-259
  /** The bound AnimData (client renderer art lookup; sim code never branches on it). */
  GetData(): AnimData | null {
    return this.m_panid;
  }

  Init(panid: AnimData): void {
    this.m_panid = panid;
    this.m_nStrip = 0;
    this.m_nFrame = 0;
    this.m_cDelay = panid.GetStripDelay(0) & 0xff;
  }

  // ht.h inlines (1740-1801)
  Stop(): void {
    this.m_wf |= kfAniDone;
  }
  IsDone(): boolean {
    return (this.m_wf & kfAniDone) !== 0;
  }
  SetStrip(nStrip: number): void {
    this.m_nStrip = nStrip;
  }
  // ht.h:1767-1776 — Animation-level wrappers: current strip, given frame (-1 = current)
  GetSpecialPoint(nFrame: number = -1): Point {
    if (nFrame === -1) nFrame = this.m_nFrame;
    const ppt: Point = { x: 0, y: 0 };
    this.m_panid!.GetSpecialPoint(this.m_nStrip, nFrame, ppt);
    return ppt;
  }

  GetSpecialPoint32(nFrame: number = -1): Point {
    if (nFrame === -1) nFrame = this.m_nFrame;
    const ppt: Point = { x: 0, y: 0 };
    this.m_panid!.GetSpecialPoint32(this.m_nStrip, nFrame, ppt);
    return ppt;
  }

  GetStrip(): number {
    return this.m_nStrip;
  }
  SetFrame(nFrame: number): void {
    this.m_nFrame = nFrame;
  }
  GetFrame(): number {
    return this.m_nFrame;
  }
  SetDelay(cDelay: number): void {
    this.m_cDelay = cDelay & 0xff;
  }
  GetFlags(): number {
    return this.m_wf;
  }
  GetRemainingFrameTime(): number {
    return this.m_cCountdown;
  }
  GetAnimationData(): AnimData | null {
    return this.m_panid;
  }

  // game/Animation.cpp:286-291 — SetStrip by NAME also reloads the countdown
  SetStripByName(pszStripName: string): void {
    this.m_nStrip = this.m_panid!.GetStripIndex(pszStripName);
    this.m_cCountdown =
      (this.m_panid!.GetStripDelay(this.m_nStrip) + this.m_panid!.GetFrameDelay(this.m_nStrip, 0)) &
      0xff;
  }

  // game/Animation.cpp:293-296
  StartByName(pszStripName: string, wf = 0): boolean {
    return this.Start(this.m_panid!.GetStripIndex(pszStripName), 0, wf);
  }

  // game/Animation.cpp:298-330
  Start(nStrip = 0, nFrame = 0, wf = 0): boolean {
    const panid = this.m_panid!;
    this.m_nStrip = nStrip;
    if (nFrame === -1) this.m_nFrame = panid.GetFrameCount(nStrip) - 1;
    else this.m_nFrame = nFrame;

    this.m_wf = (wf | (this.m_wf & kfAniFreeAnimationData)) & 0xffff;
    this.m_cCountdown =
      (panid.GetStripDelay(this.m_nStrip) + panid.GetFrameDelay(this.m_nStrip, this.m_nFrame)) &
      0xff;

    // first frame shows for at least one Update; saturating byte increment
    if (this.m_wf & kfAniIgnoreFirstAdvance) {
      if (this.m_cCountdown < 255) this.m_cCountdown++;
    }

    if (this.m_wf & kfAniLoop && panid.GetFrameCount(this.m_nStrip) === 1) {
      this.m_wf &= ~kfAniLoop;
    }

    return (this.m_wf & kfAniDone) === 0;
  }

  // game/Animation.cpp:335-373 — exactly ONE frame per expiry; excess cAdvance DISCARDED
  // (no catch-up: load-bearing for MinSkip cadence).
  Advance(cAdvance: number): boolean {
    if (this.m_wf & kfAniDone) return false;

    if (this.m_cCountdown >= cAdvance) {
      this.m_cCountdown = (this.m_cCountdown - cAdvance) & 0xff;
      return true;
    }

    if (this.m_nFrame + 1 < this.m_panid!.GetFrameCount(this.m_nStrip)) {
      this.m_nFrame++;
    } else {
      if (this.m_wf & kfAniLoop) {
        this.m_nFrame = 0;
      } else {
        this.m_wf |= kfAniDone;
        if (this.m_wf & kfAniResetWhenDone) {
          this.m_nFrame = 0;
          this.m_cCountdown = 255;
          return true;
        }
        return false;
      }
    }

    this.m_cCountdown =
      (this.m_cDelay + this.m_panid!.GetFrameDelay(this.m_nStrip, this.m_nFrame)) & 0xff;
    return true;
  }

  // game/Animation.cpp:245-271 — the owner re-Inits with the right AnimData first.
  // knVerAnimationState 1. The cross-resolution frame clamp is preserved.
  LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_nStrip = pstm.ReadByte();
    this.m_nFrame = pstm.ReadByte();
    this.m_cDelay = pstm.ReadByte();
    this.m_cCountdown = pstm.ReadByte();
    this.m_wf = pstm.ReadWord();

    const cfrm = this.m_panid!.GetFrameCount(this.m_nStrip);
    if (this.m_nFrame >= cfrm) {
      this.m_nFrame = cfrm - 1;
      this.m_cCountdown =
        (this.m_cDelay + this.m_panid!.GetFrameDelay(this.m_nStrip, this.m_nFrame)) & 0xff;
    }
    return pstm.IsSuccess();
  }

  // game/Animation.cpp:273-283
  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteByte(this.m_nStrip);
    pstm.WriteByte(this.m_nFrame);
    pstm.WriteByte(this.m_cDelay);
    pstm.WriteByte(this.m_cCountdown);
    pstm.WriteWord(this.m_wf);
    return pstm.IsSuccess();
  }

  // game/Animation.cpp:375-386
  GetRemainingStripTime(): number {
    let c = this.m_cCountdown;
    const cfrm = this.m_panid!.GetFrameCount(this.m_nStrip);
    for (let ifrm = this.m_nFrame + 1; ifrm < cfrm; ifrm++) {
      c += 1 + this.m_cDelay + this.m_panid!.GetFrameDelay(this.m_nStrip, ifrm);
    }
    return c * kctUpdate;
  }
}
